// src/Pages/Dashboard/DashboardPage.jsx
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import {
  BookOpen,
  Download,
  Star,
  Users,
  Award,
  ShoppingBag,
  Upload,
  Clock,
} from "lucide-react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify"; // Ajout de react-toastify
import "react-toastify/dist/ReactToastify.css"; // Styles pour les toasts
import config from "../../config";
import UploadVirementProof from "../../Components/Virement/UploadVirementProof";
import ImageWithAuth from "../../Components/ImageWithAuth";

const DashboardPage = ({ style }) => {
  const { i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState("overview");


  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [subscriptionCustomer, setSubscriptionCustomer] = useState([]);

  // États pour l'onglet Historique
  const [historyItems, setHistoryItems] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [historySize] = useState(12);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);

  const navigate = useNavigate();

  // Récupération du profil utilisateur
  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      const fetchUserProfile = async () => {
        try {
          const response = await axios.get(
            `${config.SITE_API_URL}/user-profile`,
            {
              headers: { Authorization: `Bearer ${token}`,
              'x-api-key': config.SITE_API_KEY },
            }
          );
          setUser(response.data?.customer);
        } catch (error) {
          console.error("Erreur profil:", error);
          localStorage.removeItem("accessToken");
          setUser(null);
          navigate("/login");
        }
      };
      fetchUserProfile();
    } else {
      navigate("/login");
    }
  }, [navigate]);

const formatDownloadDate = (dateStr) => {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  return !isNaN(date.getTime())
    ? date.toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";
};
  // ─── Pagination commandes ────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 11;

  const getUserOrders = async (page = 1) => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await axios.get(
        `${config.SITE_API_URL}/getuserorders?page=${page}&limit=${itemsPerPage}`,
        { headers: { Authorization: `Bearer ${token}`, 'x-api-key': config.SITE_API_KEY } }
      );
      setOrders(response.data.result || []);
      
      setCurrentPage(response.data.pagination?.currentPage || 1);
      setTotalPages(response.data.pagination?.totalPages || 1);
      setTotalItems(response.data.pagination?.totalItems || 0);
    } catch (error) {
      console.error("Erreur commandes:", error);
    }
  };

  const getSubscriptionCustomer = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await axios.get(
        `${config.SITE_API_URL}/getsubscriptioncustomers`,
        { headers: { Authorization: `Bearer ${token}`, 'x-api-key': config.SITE_API_KEY } }
      );
      setSubscriptionCustomer(response.data.result || []);
    } catch (error) {
      console.error("Erreur abonnements:", error);
    }
  };

  // ─── Récupération Historique paginé ──────────────────────────────────────
  const fetchHistory = async (index = 0) => {
    if (!user) return;
    setHistoryLoading(true);

    try {
      const token = localStorage.getItem("accessToken");
      const response = await axios.post(
        `${config.SITE_API_URL}/my_history`,
        {
          index,
          size: historySize,
          type: "all",
        },
        {
          headers: { Authorization: `Bearer ${token}`, 'x-api-key': config.SITE_API_KEY },
        }
      );

      if (response.data.success) {
        setHistoryItems(response.data.result || []);
        setHistoryTotal(response.data.total || 0);
      }
    } catch (error) {
      console.error("Erreur historique:", error);
      toast.error("Impossible de charger l'historique");
    } finally {
      setHistoryLoading(false);
    }
  };

  // ─── Calcul des activités récentes ───────────────────────────────────────
  const recentActivity = orders
    .flatMap((order) => {
      const purchaseItems = order.items?.map((item) => ({
        id: `purchase-${order._id}-${item._id}`,
        type: "purchase",
        title: `Achat de "${item.documentId?.title?.fr || "Document"}"`,
        date: order.datePaiement || order.createdAt || new Date().toISOString(),
        icon: ShoppingBag,
        sortDate: new Date(order.datePaiement || order.createdAt),
      })) || [];

      const downloadItems = order.items
        ?.filter((item) => item.isActive && item.datedownload)
        .map((item) => ({
          id: `download-${order._id}-${item._id}`,
          type: "download",
          title: `Téléchargement de "${item.documentId?.title?.fr || "Document"}"`,
          date: item.datedownload,
          icon: Download,
          sortDate: new Date(item.datedownload),
        })) || [];

      return [...purchaseItems, ...downloadItems];
    })
    .sort((a, b) => b.sortDate - a.sortDate)
    .slice(0, 3)
    .map(({ sortDate, ...act }) => act);

  // ─── Fonction de couleur de statut ───────────────────────────────────────
  const getStatusColor = (status) => {
    switch (status) {
      case "terminée":
        return "bg-red-100 text-red-800";
      case "en cours":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // ─── Effets ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (user) {
      getUserOrders(currentPage);
    }
  }, [user, currentPage]);

  useEffect(() => {
    if (activeTab === "subscription") {
      getSubscriptionCustomer();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "history") {
      fetchHistory(historyIndex);
    }
  }, [activeTab, historyIndex, user]);

  const handleDownload = async (fileUrl, fileName, itemId, orderId) => {
  try {
    const token = localStorage.getItem("accessToken");
    
    // Solution 2: Utiliser axios avec les headers corrects
    const response = await axios.get(fileUrl, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'x-api-key': config.SITE_API_KEY
      },
      responseType: 'blob'
    });
    
    // Créer un lien de téléchargement
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName || "document";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    toast.success("Téléchargement réussi");
    
    // Marquer comme téléchargé après un court délai
    setTimeout(async () => {
      await axios.put(
        `${config.SITE_API_URL}/${orderId}/item/${itemId}/activate`,
        {},
        { 
          headers: { 
            Authorization: `Bearer ${token}`, 
            'x-api-key': config.SITE_API_KEY 
          } 
        }
      );
      
      setOrders((prev) =>
        prev.map((order) =>
          order._id === orderId
            ? {
                ...order,
                items: order.items.map((item) =>
                  item._id === itemId ? { ...item, isActive: true } : item
                ),
              }
            : order
        )
      );
    }, 1000);
    
  } catch (error) {
    console.error("Erreur téléchargement:", error);
    toast.error("Échec du téléchargement");
  }
};

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-xl text-gray-600">Chargement...</p>
      </div>
    );
  }

  // Statistiques
  const totalDocs = orders.reduce((sum, o) => sum + (o.items?.length || 0), 0);
  const downloadedCount = orders.reduce(
    (count, o) =>
      count +
      (o.items?.filter((item) => item.isActive === true).length || 0),
    0
  );

  const stats = [
    { icon: BookOpen, label: "Documents achetés", value: totalDocs, color: "bg-blue-500" },
    { icon: Download, label: "Téléchargements", value: downloadedCount, color: "bg-green-500" },
    { icon: Star, label: "Abonnements", value: subscriptionCustomer.length, color: "bg-yellow-500" },
  ];
  const paymentStatusMap = {
  Succès:     { label: "Payé",                color: "bg-green-100 text-green-800" },
  completed:  { label: "Payé",                color: "bg-green-100 text-green-800" },
  pending:    { label: "En attente",          color: "bg-amber-100 text-amber-800" },
  pending_proof: { label: "En attente de preuve", color: "bg-amber-100 text-amber-800" },
  Échouée:    { label: "Échoué",              color: "bg-red-100 text-red-800" },
};
  return (
    <div
      className="min-h-screen bg-gray-50"
      style={{ ...style }}
    >
      <div
        className="max-w-[80%] mx-auto px-2 min-sm:px-6 min-lg:px-8 min-lg:py-8 min-lg:mt-[4%] py-24 mt-[12%]"
        style={{ ...style }}
      >
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <img
              src="/logo-Jaafar-Academy-1.png"
              alt={`${user.first_name_parent || ""} ${user.last_name_parent || ""}`}
              className="h-16 w-16 rounded-full"
            />
            <div>
              <p className="text-lg font-bold text-gray-900">
                Bonjour, {`${user.first_name_parent || ""} ${user.last_name_parent || ""}`}
              </p>
              <p className="text-gray-600">{user.parent_phone}</p>
            </div>
          </div>
          <ToastContainer position="bottom-left" />

          {/* Navigation Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex flex-col min-sm:flex-row space-x-8">
              <button
                onClick={() => setActiveTab("overview")}
                className={`pb-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === "overview"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                Vue d'ensemble
              </button>
              <button
                onClick={() => setActiveTab("orders")}
                className={`pb-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === "orders"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
                aria-label="Mes Documents"
              >
                Mes Documents
              </button>
              <button
                onClick={() => setActiveTab("subscription")}
                className={`pb-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === "subscription"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
                aria-label="Mes Abonnements"
              >
                Mes Abonnements
              </button>
              <button
                onClick={() => setActiveTab("history")}
                className={`pb-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === "history"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
                aria-label="Mon Historique"
              >
                Mon Historique
              </button>
            </nav>
          </div>
        </div>

        {/* Content */}
        <div>
          {activeTab === "overview" && (
            <div className="space-y-8">
              {/* Stats */}
              <div className="grid grid-cols-1 min-md:grid-cols-2 min-lg:grid-cols-3 gap-6">
                {stats.map((stat, index) => (
                  <div
                    key={stat._id}
                    className="bg-white rounded-lg shadow-md p-6"
                  >
                    <div className="flex items-center">
                      <div className={`${stat.color} p-3 rounded-lg`}>
                        <stat.icon className="h-6 w-6 text-white" />
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">
                          {stat.label}
                        </p>
                        <p className="text-2xl font-bold text-gray-900">
                          {stat.value}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Actions rapides
                </h2>
                <div className="grid grid-cols-1 min-md:grid-cols-3 gap-4">
                  <Link
                    to="/course"
                    className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <BookOpen className="h-6 w-6 text-blue-600" />
                    <span className="font-medium">Explorer les cours</span>
                  </Link>
                  <Link
                    to="/profile"
                    className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Users className="h-6 w-6 text-green-600" />
                    <span className="font-medium">Modifier mon profil</span>
                  </Link>
                  <Link
                    to="#"
                    className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Award className="h-6 w-6 text-purple-600" />
                    <span className="font-medium">Centre d'aide</span>
                  </Link>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Activité récente
                </h2>
                <div className="space-y-4">
                  {recentActivity.length > 0 ? (
                    recentActivity.map((activity) => (
                      <div
                        key={activity.id}
                        className="flex items-center space-x-4 p-3 hover:bg-gray-50 rounded-lg"
                      >
                        <div className="bg-gray-100 p-2 rounded-lg">
                          <activity.icon className="h-5 w-5 text-gray-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">
                            {activity.title}
                          </p>
                          <p className="text-sm text-gray-500">
                            {new Date(activity.date).toLocaleDateString(
                              "fr-FR"
                            )}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-center py-4">
                      Aucune activité récente
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === "orders" && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900">
                  Mes Documents
                </h2>
                <Link
                  to="/course"
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Nouvelle commande
                </Link>
              </div>

              {orders.length === 0 ? (
                <div className="bg-white rounded-lg shadow-md p-12 text-center">
                  <ShoppingBag className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Aucune commande
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Commencez par explorer notre Document pour passer une
                    commande.
                  </p>
                  <Link
                    to="/course"
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Explorer le Document
                  </Link>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 min-md:grid-cols-2 min-lg:grid-cols-3 gap-6">
                    {orders.map((order) =>
                      order?.items?.map((item) => {
                        const isVirementPending =
                          order.paymentMethod === "virement" &&
                          order.paymentStatus === "pending";

                        return (
                          <div
                            key={item._id}
                            className="m-2 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 p-4 overflow-hidden"
                          >
                            <ImageWithAuth
                              src={`${config.SITE_API_URL}${item?.documentId?.picture}`}
                              alt={item?.documentId?.title?.fr}
                              className="w-full h-48 object-cover"
                            />
                            <div className="p-4">
                              <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                                {item?.documentId?.title?.fr}
                              </h3>
                              <p className="text-sm text-gray-600 mb-3">
                                Référence: {item?.documentId?.reference}
                              </p>
                              <p className="text-base font-bold text-gray-600 mb-2">
                                Prix: {item.price_ttc.toFixed(3)} DNT
                              </p>
                              <div className="flex items-center justify-between mt-4">
                                {/* Afficher le statut */}
                                <span
                                  className={`text-sm font-medium ${
                                    item.isActive
                                      ? "text-green-600"
                                      : "text-orange-600"
                                  }`}
                                >
                                  {item.isActive ? (
                                    <span className="text-green-600 text-sm font-medium">
                                      Téléchargé le {formatDownloadDate(item.datedownload)}
                                    </span>
                                  ) : order.paymentStatus === "Succès" ? (
                                    <span className="text-orange-600 text-sm font-medium">
                                      Prêt à télécharger
                                    </span>
                                  ) : isVirementPending ? (
                                    // Si virement pending SANS preuve
                                    <span className="text-red-600 text-sm font-medium">
                                      En attente de votre paiement
                                    </span>
                                  ) : (
                                    // Autres cas de pending (konnect, flouci)
                                    <span className="text-red-600 text-sm font-medium">
                                      En attente de Vérification Administratif
                                    </span>
                                  )}
                                </span>

                                {/* Upload preuve virement (si commande virement pending) */}
                                {isVirementPending && (
                                  <UploadVirementProof
                                    orderId={order._id}
    orderCode={order.code_orders}
    isSubscription={false}
    uploadAttempts={order.uploadAttempts || 0}
    maxAttempts={3}
                                  />
                                )}

                                {/* Bouton visible SEULEMENT si isActive === false */}
                                {!item.isActive &&
                                  order.paymentStatus === "Succès" && (
                                    <button
                                      onClick={() =>
                                        handleDownload(
                                          `${config.SITE_API_URL}/${item.documentId.file}`,
                                          item.documentId.file || "document",
                                          item._id, // itemId
                                          order._id // orderId
                                        )
                                      }
                                      className="flex items-center bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors"
                                    >
                                      <Download className="h-4 w-4 mr-1" />
                                      Télécharger
                                    </button>
                                  )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* Contrôles de pagination */}
                  {totalPages > 1 && (
                    <div className="mt-8 flex justify-center items-center gap-4">
                      <button
                        onClick={() =>
                          setCurrentPage((p) => Math.max(p - 1, 1))
                        }
                        disabled={currentPage === 1}
                        className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
                      >
                        ← Précédent
                      </button>

                      <span className="text-gray-700 font-medium">
                        Page {currentPage} sur {totalPages}
                      </span>

                      <button
                        onClick={() =>
                          setCurrentPage((p) => Math.min(p + 1, totalPages))
                        }
                        disabled={currentPage === totalPages}
                        className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
                      >
                        Suivant →
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {activeTab === "subscription" && (
  <div className="space-y-6">
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
      <h2 className="text-xxl font-bold text-gray-900">
        Mes Abonnements
      </h2>

      <Link
        to="/subscription"
        className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-5 py-2.5 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-md hover:shadow-lg text-sm font-medium flex items-center gap-2"
      >
        <ShoppingBag className="w-4 h-4" />
        Souscrire à un nouveau pack
      </Link>
    </div>

    {subscriptionCustomer.length === 0 ? (
      <div className="bg-white rounded-xl shadow-md p-10 text-center border border-gray-100">
        <div className="mx-auto w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mb-6">
          <Award className="w-10 h-10 text-blue-500" />
        </div>
        <h3 className="text-xl font-semibold text-gray-800 mb-3">
          Vous n'avez pas encore d'abonnement actif
        </h3>
        <p className="text-gray-600 mb-8 max-w-md mx-auto">
          Choisissez un pack d'abonnement pour accéder à des milliers de documents sans limite.
        </p>
        <Link
          to="/subscription"
          className="inline-flex items-center bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-all shadow-md hover:shadow-lg font-medium"
        >
          Voir les packs disponibles
        </Link>
      </div>
    ) : (
      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
        <div className="overflow-x-auto">
          <table className="w-full min-w-max">
            <thead className="bg-gradient-to-r from-blue-50 to-cyan-50">
              <tr>
                <th className="p-4 text-left text-sm font-semibold text-blue-900 whitespace-nowrap">
                  N° Pack
                </th>
                <th className="p-4 text-left text-sm font-semibold text-blue-900 whitespace-nowrap">
                  Nom du Pack
                </th>
                <th className="p-4 text-center text-sm font-semibold text-blue-900 whitespace-nowrap">
                  Nb docs pack
                </th>
                <th className="p-4 text-center text-sm font-semibold text-blue-900 whitespace-nowrap">
                  Docs restants
                </th>
                <th className="p-4 text-center text-sm font-semibold text-blue-900 whitespace-nowrap">
                  Prix total
                </th>
                <th className="p-4 text-center text-sm font-semibold text-blue-900 whitespace-nowrap">
                  Date début
                </th>
                <th className="p-4 text-center text-sm font-semibold text-blue-900 whitespace-nowrap">
                  Date fin
                </th>
                <th className="p-4 text-center text-sm font-semibold text-blue-900 whitespace-nowrap">
                  État
                </th>
                <th className="p-4 text-center text-sm font-semibold text-blue-900 whitespace-nowrap">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {subscriptionCustomer.map((sub) => {
                const isVirementPending =
                  sub.paymentMethod === "virement" && sub.paymentStatus === "pending";

                const isOtherPending =
                  sub.paymentStatus === "pending_proof" && !isVirementPending;

                let statusText = sub.etat_subscription;
                let statusColorClass = getStatusColor(sub.etat_subscription);

                // Surcharge pour les virements et pending
                if (isVirementPending) {
                  statusText = "En attente de votre paiement";
                  statusColorClass = "bg-amber-100 text-amber-800";
                } else if (isOtherPending) {
                  statusText = "En attente de vérification administrative";
                  statusColorClass = "bg-purple-100 text-purple-800";
                }

                return (
                  <tr
                    key={sub._id}
                    className="hover:bg-blue-50/30 transition-colors"
                  >
                    <td className="p-4 font-medium text-gray-900">
                      {sub?.code_subscription || "—"}
                    </td>
                    <td className="p-4 text-gray-700">
                      {sub?.subscriptionId?.title?.[i18n.language] ||
                        sub?.subscriptionId?.title?.fr ||
                        "Pack sans nom"}
                    </td>
                    <td className="p-4 text-center text-gray-700">
                      {sub?.subscriptionId?.nbr_doc || "—"}
                    </td>
                    <td className="p-4 text-center text-gray-700 font-medium">
                      {sub?.number_document ?? "—"}
                    </td>
                    <td className="p-4 text-center text-gray-700">
                      {sub?.subscriptionId?.price_ttc.toFixed(3)} DT
                    </td>
                    <td className="p-4 text-center text-gray-600 text-sm">
                      {sub?.date_subscription
                        ? new Date(sub.date_subscription).toLocaleDateString("fr-FR")
                        : "—"}
                    </td>
                    <td className="p-4 text-center text-gray-600 text-sm">
                      {sub?.date_fin_subscription
                        ? new Date(sub.date_fin_subscription).toLocaleDateString("fr-FR")
                        : "—"}
                    </td>
                    <td className="p-4 text-center">
                      <span
                        className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${statusColorClass}`}
                      >
                        {statusText}
                      </span>
                    </td>
                    <td className="p-4 text-center">
                      {isVirementPending && (
                        <div className="flex justify-center">
                          <UploadVirementProof
    orderId={sub._id}
    orderCode={sub.code_subscription}
    isSubscription={true}
    uploadAttempts={sub.uploadAttempts || 0}
    maxAttempts={3}
                            className="text-xs bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded-md transition-colors flex items-center gap-1.5"
                          >
                            <Upload className="w-3.5 h-3.5" />
                            Uploader preuve
                          </UploadVirementProof>
                        </div>
                      )}

                      {isOtherPending && (
                        <span className="text-xs text-purple-700 italic">
                          En cours de traitement
                        </span>
                      )}

                      {sub.etat_subscription === "en cours" && (
                        <span className="text-xs text-green-700 font-medium">
                          Actif
                        </span>
                      )}

                      {sub.etat_subscription === "terminée" && (
                        <span className="text-xs text-red-700 font-medium">
                          Expiré
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Légende des statuts (optionnel mais utile) */}
        <div className="p-4 bg-gray-50 border-t border-gray-100 text-sm text-gray-600">
          <div className="flex flex-wrap gap-6 justify-center md:justify-start">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-green-500"></span>
              <span>Actif / en cours</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-amber-500"></span>
              <span>En attente de votre virement</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-purple-500"></span>
              <span>En vérification administrative</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-red-500"></span>
              <span>Terminé / expiré</span>
            </div>
          </div>
        </div>
      </div>
    )}
  </div>
)}
        {activeTab === "history" && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">Historique complet</h2>
              <div className="text-sm text-gray-600">
                {historyTotal} élément{historyTotal !== 1 ? "s" : ""}
              </div>
            </div>

            {historyLoading ? (
              <p className="text-center py-12 text-gray-500">Chargement...</p>
            ) : historyItems.length === 0 ? (
              <div className="bg-white rounded-xl p-12 text-center shadow border">
                <Clock className="mx-auto h-16 w-16 text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Historique vide</h3>
                <p className="text-gray-600 mb-6">
                  Vos transactions apparaîtront ici.
                </p>
                <Link
                  to="/course"
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
                >
                  Explorer maintenant
                </Link>
              </div>
            ) : (
              <>
                <div className="bg-white rounded-xl shadow overflow-hidden border">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">
                            Type
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">
                            Référence
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">
                            Description
                          </th>
                          <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase">
                            Montant
                          </th>
                          <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase">
                            Date
                          </th>
                          <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase">
                            Statut
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {historyItems.map((item) => (
                          <tr key={item.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                                  item.type === "subscription"
                                    ? "bg-purple-100 text-purple-800"
                                    : "bg-blue-100 text-blue-800"
                                }`}
                              >
                                {item.type === "subscription" ? "Abonnement" : "Commande"}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap font-medium">
                              {item.code || "—"}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900">
                              {item.type === "subscription"
                                ? item.name || "Pack sans nom"
                                : `${item.itemsCount || 0} document${(item.itemsCount || 0) > 1 ? "s" : ""}`}
                            </td>
                            <td className="px-6 py-4 text-center font-medium">
                              {(item.totalPrice || 0).toFixed(3)} DT
                            </td>
                            <td className="px-6 py-4 text-center text-sm text-gray-500">
                              {new Date(item.paymentDate || item.createdAt).toLocaleDateString("fr-FR")}
                            </td>
                            <td className="px-6 py-4 text-center whitespace-nowrap">
  <span
    className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${
      paymentStatusMap[item.paymentStatus]?.color || "bg-gray-100 text-gray-700"
    }`}
  >
    {paymentStatusMap[item.paymentStatus]?.label || (item.paymentStatus || "—")}
  </span>
</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
{historyTotal > historySize && (
          <div className="mt-8 flex justify-center items-center gap-4">
            <button
              onClick={() => setHistoryIndex((i) => Math.max(0, i - 1))}
              disabled={historyIndex === 0}
              className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
            >
              ← Précédent
            </button>

            <span className="text-gray-700 font-medium">
              Page {historyIndex + 1} sur {Math.ceil(historyTotal / historySize)}
            </span>

            <button
              onClick={() =>
                setHistoryIndex((i) =>
                  i + 1 < Math.ceil(historyTotal / historySize) ? i + 1 : i
                )
              }
              disabled={historyIndex + 1 >= Math.ceil(historyTotal / historySize)}
              className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
            >
              Suivant →
            </button>
          </div>
        )}
              </>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
